// Dental diagnosis catalogue for the Part Conditions chart modal.
//
// THERE IS NO DIAGNOSIS CATALOGUE TABLE. Diagnosis is a free-text `label` on
// `patient_diagnosis_entries`, and the existing "catalogue" is
// `qDiagnosisSuggestions` — `SELECT DISTINCT label` over the org's own past
// entries. That works well once a clinic has history and offers nothing on day
// one, which is why the chart needs a starting list.
//
// So this is a SEED for that same suggestion mechanism, not a second diagnosis
// model: entries chosen here are stored exactly like typed ones, and a clinic's
// own past labels keep flowing through unchanged. No schema, no migration.
//
// Import-free so it runs under node:test.

/**
 * Initial dental diagnosis options.
 *
 * Labels are stored VERBATIM as typed here, because they become the `label` of
 * a `patient_diagnosis_entries` row and matching against past usage is by exact
 * string.
 *
 * Three entries arrived from the source list misspelled and have since been
 * corrected — "Attiriton" → "Attrition", "Grossy" → "Grossly", and
 * "Carries" → "Caries", which also makes the entry reachable by a search for
 * "caries". Rows ALREADY SAVED under the old spellings are deliberately left
 * untouched: they are historical clinical text, not catalogue references, so
 * they still load and print exactly as recorded. They simply stop being
 * offered as new suggestions.
 */
export const DENTAL_DIAGNOSIS_CATALOG: readonly string[] = [
  'Abrasion',
  'Abscess',
  'Attrition',
  'Bruxism',
  'Calculus / Staining',
  'CD',
  'Complete Edentulism',
  'Dental Caries',
  'Dental tissue neoplasm',
  'Discolored',
  'Dislodged Restoration',
  'Dry mouth',
  'Fissure Caries',
  'Fluorosis',
  'Food Impaction',
  'Fracture',
  'Fractured Restoration',
  'Generalized Attrition / Wear',
  'Grossly Decayed',
  'Halitosis',
  'Impacted tooth',
  'Implants',
  'Malocclusion',
  'Malocclusion / Crowding',
  'Old Crown / Bridge',
  'Old restoration(s)',
  'Periapical abscess',
  'Periapical Periodontitis',
  'Perio Pocket',
  'Periodontitis / Gingivitis',
  'Proximal Dental Caries',
  'RC Treated',
  'Recession',
  'Recurrent Caries',
  'Root Stumps',
  'RPD',
  'Sensitivity',
  'Spacing and Rotation',
  'Tenderness',
  'TMJ disorder',
  'White spot lesion',
];

/**
 * Merge the seed catalogue with the org's own past diagnosis labels.
 *
 * The org's labels come FIRST: a clinic's established wording is more relevant
 * than a generic list, and it is what their other encounters already read.
 *
 * De-duplication is case-insensitive, so an org that has typed "dental caries"
 * does not see it twice next to the catalogue's "Dental Caries". The org's own
 * casing wins for the same reason it is ordered first.
 *
 * `search` filters both sources identically, so the seeded options behave like
 * any other suggestion as the user types.
 */
/** Slots kept for the org's own labels — matches `qDiagnosisSuggestions`' LIMIT. */
const ORG_LABEL_RESERVE = 20;

export function mergeDiagnosisSuggestions(
  orgLabels: readonly string[],
  search: string,
  limit = 40,
  /**
   * Put the curated catalogue ahead of the org's own labels.
   *
   * For the DENTAL CHART this is the right order: `patient_diagnosis_entries`
   * is shared with every other specialty, so an org's history is full of
   * dermatology and general-medicine text that is noise on a tooth. The
   * catalogue is the list a dentist is actually choosing from; org labels stay
   * available underneath.
   */
  catalogFirst = false,
): string[] {
  // In catalogue-first mode the catalogue alone can fill `limit` — it is
  // exactly as long as the default — which would push the org's own wording
  // off the end entirely. Reserve a tail for it, sized to match
  // `qDiagnosisSuggestions`' own LIMIT so nothing that query returns is lost.
  const effectiveLimit = catalogFirst
    ? Math.max(limit, DENTAL_DIAGNOSIS_CATALOG.length + ORG_LABEL_RESERVE)
    : limit;

  const needle = search.trim().toLowerCase();
  const seen = new Set<string>();
  const out: string[] = [];

  const ordered = catalogFirst
    ? [...DENTAL_DIAGNOSIS_CATALOG, ...orgLabels]
    : [...orgLabels, ...DENTAL_DIAGNOSIS_CATALOG];

  for (const label of ordered) {
    const trimmed = (label ?? '').trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    if (needle && !key.includes(needle)) continue;
    seen.add(key);
    out.push(trimmed);
    if (out.length >= effectiveLimit) break;
  }
  return out;
}

/**
 * Narrow a suggestion list against what the user has typed so far.
 *
 * Case-insensitive SUBSTRING matching: "peri" must reach "Periapical abscess"
 * and "caries" must reach "Fissure Caries", neither of which prefix matching
 * would find.
 *
 * An entry identical to the current text is dropped — offering the user their
 * own exact input back is a dead row. Everything else is left alone, so a value
 * that matches nothing simply yields an empty list and the field stays free
 * text.
 */
export function filterCatalogSuggestions(suggestions: readonly string[], typed: string): string[] {
  const needle = typed.trim().toLowerCase();
  return suggestions.filter((s) => {
    const lower = (s ?? '').trim().toLowerCase();
    if (!lower || lower === needle) return false;
    return !needle || lower.includes(needle);
  });
}
